import { useCallback, useEffect, useRef, useState } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { JSONNode } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type {
	StreamActionClassification,
	StreamActionPart,
	StreamActionPrepare,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	isStoreError,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type {
	StoreAssistant,
	StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useConversationById } from '@atlassian/conversation-assistant/controllers/chat-store';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isRemixRestrictedNodeType } from '../remix/remix-config';
import {
	getSubTypeDisplayName,
	getTypeDisplayName,
	type RemixOptionSelectionParameters,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../remix/remix-overlay';

import { useEditorRemixContext } from './context';
import type { DynamicUiSource, RemixProductConfig, RemixProductIntegration } from './types';
import {
	getContentClassificationFromActionClassification,
	getFinalAIFCMessage,
	withWhiteboardCreateActionData,
} from './utils/message';
import { getFinalMessageId } from './utils/remix-ids';

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 */
export const filterRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isRemixRestrictedNodeType(adfNode.type)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

const isActionComplete = (chatMessages: StoreChatItem[]): boolean => {
	const hasWhiteboard =
		findLastAssistantAction(
			chatMessages,
			(action) =>
				action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
				action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
		) !== undefined;
	if (hasWhiteboard) {
		return true;
	}
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_COMPLETE') {
			return true;
		}
		if (message.type === 'HUMAN') {
			return false;
		}
	}
	return false;
};

const defaultShouldUseEphemeralPreview = (chatMessages: StoreChatItem[]): boolean => {
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_PREPARE') {
			return message.action ? isRemixGeneratedFlow(message.action) : false;
		}
	}
	return false;
};

const defaultIsActionReady = (chatMessages: StoreChatItem[]): boolean =>
	isActionComplete(chatMessages);

export const useRemixToAIFC = ({
	config,
	onActionReady,
	productIntegration,
}: {
	config: RemixProductConfig;
	onActionReady?: () => void;
	productIntegration: RemixProductIntegration;
}): {
	closeRemixPreview: () => void;
	isRemixAborted: boolean;
	isRemixError: boolean;
	openRemixPreview: () => void;
	remixStoreError: StoreError | null;
	sendAIFCPrompt: ({
		userPrompt,
		dynamicUiSubtype,
		dynamicUiType,
		dynamicUiSource,
		interactionSource,
		aspectRatio,
	}: {
		aspectRatio?: string | null;
		dynamicUiSource: DynamicUiSource | undefined;
		dynamicUiSubtype?: string;
		dynamicUiType: RemixOptionSelectionParameters['type'];
		interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
		userPrompt?: string;
	}) => Promise<void>;
	stopRemixing: () => void;
} => {
	const {
		isUpdate,
		conversationId,
		setConversationId,
		setFinalMessage,
		setContentClassification,
		remixUpdateSentinelRef,
	} = useEditorRemixContext();
	const experienceTracker = useAIMateExperienceTracker();
	const { sendMessage, createConversationOptimistically, abortRequests, activeConversation } =
		useSmartCreationChatStoreApi();
	const { setPrompt, setDynamicUiContext, setForceSkipAutoConversationSelection } =
		useStagingAreaActions();
	const { preferences } = useUserPreferences();
	// remove local state when cleanup platform_editor_ai_remix_in_editor_loading
	const [remixConversationId, setRemixConversationId] = useState<string | undefined>(undefined);

	const activeRemixConversation =
		activeConversation?.id && activeConversation.id === remixConversationId
			? activeConversation
			: null;
	const remixConversationById = useConversationById({
		conversationId: conversationId ?? '',
	});
	const remixConversation = fg('platform_editor_ai_remix_in_editor_loading')
		? remixConversationById
		: activeRemixConversation;
	const remixConversationIdToRead = fg('platform_editor_ai_remix_in_editor_loading')
		? conversationId
		: activeRemixConversation?.id;

	const [chatMessages] = useChatMessages({ conversationId: remixConversationIdToRead ?? '' });
	let finalMessage = fg('platform_editor_ai_remix_in_editor_loading')
		? getFinalAIFCMessage(chatMessages)
		: undefined;

	// If it's an update, suppress the previously-displayed result until the new
	// variant response arrives. The sentinel is the id of the final message that
	// was on the page when the update was initiated, captured live in
	// useUpdateRemix — stored remix-history ids can be stale (e.g. a BE-driven Ask
	// Rovo edit inherits the prior entry's id rather than its own message id).
	const currentFinalMessageId = getFinalMessageId(finalMessage);
	const previousFinalMessageId = remixUpdateSentinelRef.current;
	if (
		isUpdate &&
		previousFinalMessageId &&
		previousFinalMessageId === currentFinalMessageId &&
		fg('platform_editor_ai_remix_toolbar')
	) {
		finalMessage = undefined;
	}

	const remixStoreError =
		chatMessages.find((message): message is StoreError => isStoreError(message)) ?? null;
	const isRemixError =
		remixConversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ||
		remixStoreError !== null;

	const isRemixAborted =
		!isRemixError &&
		remixConversation?.status === CHAT_STORE_STATUS.idle &&
		!isActionComplete(chatMessages) &&
		chatMessages.some(isStoreAssistant);

	const actionReadyFired = useRef(false);
	const recordedFinalMessageId = useRef<string | undefined>(undefined);
	const recordedFinalMessage = useRef<StoreAssistant | undefined>(undefined);
	const previousConversationId = useRef(remixConversationIdToRead);

	useEffect(() => {
		if (previousConversationId.current !== remixConversationIdToRead) {
			actionReadyFired.current = false;
			recordedFinalMessageId.current = undefined;
			recordedFinalMessage.current = undefined;
			previousConversationId.current = remixConversationIdToRead;
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setFinalMessage(undefined);
			}
		}
	}, [remixConversationIdToRead, setFinalMessage]);

	useEffect(() => {
		if (actionReadyFired.current || !onActionReady) {
			return;
		}

		const isActionReady =
			config.isActionReady?.(chatMessages) ?? defaultIsActionReady(chatMessages);

		if (isActionReady) {
			actionReadyFired.current = true;
			onActionReady();
		}
	}, [config, chatMessages, onActionReady]);

	useEffect(() => {
		const finalMessageId = getFinalMessageId(finalMessage);
		const hasRecordedFinalMessage =
			finalMessageId !== undefined
				? recordedFinalMessageId.current === finalMessageId
				: recordedFinalMessage.current === finalMessage;
		if (
			finalMessage &&
			!hasRecordedFinalMessage &&
			fg('platform_editor_ai_remix_in_editor_loading')
		) {
			recordedFinalMessageId.current = finalMessageId;
			recordedFinalMessage.current = finalMessage;
			setFinalMessage(finalMessage);
			experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		}
	}, [finalMessage, setFinalMessage, experienceTracker]);

	const stopRemixing = useCallback(() => {
		abortRequests?.({ conversationId: remixConversationIdToRead });
	}, [abortRequests, remixConversationIdToRead]);

	const openRemixPreview = useCallback(() => {
		setForceSkipAutoConversationSelection(true);
		const shouldUseEphemeralPreview =
			config.shouldUseEphemeralPreview?.(chatMessages) ??
			defaultShouldUseEphemeralPreview(chatMessages);

		productIntegration.openRemixPreview({
			isFloating: shouldUseEphemeralPreview,
			remixConversationId: remixConversationIdToRead,
		});
	}, [
		config,
		chatMessages,
		productIntegration,
		remixConversationIdToRead,
		setForceSkipAutoConversationSelection,
	]);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			dynamicUiSubtype,
			dynamicUiType,
			dynamicUiSource,
			interactionSource,
			aspectRatio,
		}: {
			aspectRatio?: string | null;
			dynamicUiSource: DynamicUiSource | undefined;
			dynamicUiSubtype?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
			userPrompt?: string;
		}) => {
			const defaultPromptText = 'Remix the selected content';
			const selectedDisplayName = dynamicUiSubtype
				? getSubTypeDisplayName(dynamicUiSubtype)
				: getTypeDisplayName(dynamicUiType);
			const promptText =
				dynamicUiType === 'custom' || (dynamicUiType === 'image' && userPrompt)
					? userPrompt ?? defaultPromptText
					: `Remix the selected content as a ${selectedDisplayName}`;

			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId = crypto.randomUUID();
			setRemixConversationId(newConversationId);
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				setConversationId(newConversationId);
				setFinalMessage(undefined);
				setContentClassification(undefined);
			}
			actionReadyFired.current = false;

			await createConversationOptimistically({ id: newConversationId });

			const creationContextParamsToSend = {
				dynamicUiType,
				dynamicUiSubtype,
				dynamicUiSource,
				source: 'remix' as const,
				...(aspectRatio ? { additionalContext: { aspectRatio } } : {}),
			};

			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource,
					followUpSource: interactionSource,
				},
				promptStreamHook: fg('platform_editor_ai_remix_in_editor_loading')
					? {
							onActionClassification: async (
								actionClassification: StreamActionClassification,
							) => {
								setContentClassification(
									getContentClassificationFromActionClassification(actionClassification),
								);
							},
							onActionPart: async (actionPart: StreamActionPart | StreamActionPrepare) => {
								// ACTION_CLASSIFICATION is the semantic source of truth for the stream's
								// content type. ACTION_PREPARE may carry a contentId, but it does not yet
								// represent visible whiteboard output. Wait for the first real ACTION_PART
								// before inserting the whiteboard so users do not see an empty embed.
								if (
									actionPart.type === 'ACTION_PART' &&
									actionPart.message.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY
								) {
									setContentClassification((classification) =>
										withWhiteboardCreateActionData(classification, actionPart.message.data),
									);
								}
							},
						}
					: undefined,
				productKey: config.productKey,
				contentContext: 'staging-area',
				creationContextParams: creationContextParamsToSend,
			});

			setPrompt('Remix');
			setDynamicUiContext(dynamicUiType, dynamicUiSubtype, dynamicUiSource);
		},
		[
			setConversationId,
			config.productKey,
			createConversationOptimistically,
			sendMessage,
			setPrompt,
			setDynamicUiContext,
			preferences.storeUserMessages,
			setFinalMessage,
			setContentClassification,
		],
	);

	return {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		isRemixAborted,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview: productIntegration.closeRemixPreview,
	};
};
